// ActionDetailPage — T2 (Object Detail + Tabs) layout.
// Displays a single action with tabbed sections.
// Route: /t/:slug/actions/:actionId (and sub-routes for tabs)

import { useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  actionsApi,
  actionLifecycleStateLabels,
  actionLifecycleStateColors,
} from '@/lib/api/actions'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { DetailPageShell, type TabDef } from '@/components/shared/DetailPageShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { MoveActionDialog } from './MoveActionDialog'
import { User, Calendar, FolderOpen, AlertTriangle, MoveRight } from 'lucide-react'
import { cn } from '@/lib/utils'

const tabs: TabDef[] = [
  { id: 'detail', label: 'Detail', path: '' },
  { id: 'updates', label: 'Updates', path: '/updates', countKey: 'update_count' },
  { id: 'controls', label: 'Controls', path: '/controls', countKey: 'linked_control_count' },
  { id: 'events', label: 'Events', path: '/events', countKey: 'linked_event_count' },
  { id: 'risks', label: 'Risks', path: '/risks', countKey: 'linked_risk_count' },
  { id: 'evidence', label: 'Evidence', path: '/evidence' },
  { id: 'history', label: 'Audit Log', path: '/history' },
]

export function ActionDetailPage() {
  const { slug } = useTenant()
  const { actionId } = useParams({ strict: false }) as { actionId: string }
  const api = useApi()
  const actApi = actionsApi(api)
  const [moveOpen, setMoveOpen] = useState(false)

  const {
    data: action,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.actions.detail(slug, actionId),
    queryFn: () => actApi.get(actionId),
  })

  if (isLoading) return <FormSkeleton />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load action"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (!action) return null

  return (
    <DetailPageShell
      title={action.title}
      code={action.short_code || undefined}
      stateLabel={actionLifecycleStateLabels[action.lifecycle_state] ?? action.lifecycle_state}
      stateColorClass={actionLifecycleStateColors[action.lifecycle_state]}
      backUrl={`/t/${slug}/actions`}
      backLabel="Back to action list"
      tabs={tabs}
      basePath={`/t/${slug}/actions/${actionId}`}
      data={action as unknown as Record<string, unknown>}
      actions={
        <>
          <Button variant="outline" size="sm" onClick={() => setMoveOpen(true)}>
            <MoveRight className="mr-1.5 h-3.5 w-3.5" />
            Move to...
          </Button>
          <MoveActionDialog
            open={moveOpen}
            onClose={() => setMoveOpen(false)}
            actionId={actionId}
            currentCategoryId={action.category_id}
            currentCategoryName={action.category_name}
            onMoved={() => setMoveOpen(false)}
          />
        </>
      }
      metadata={
        <>
          <span className="flex items-center gap-1">
            <FolderOpen className="h-3.5 w-3.5" />
            {action.category_name}
          </span>
          {action.owner_display_name && (
            <span className="flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              {action.owner_display_name}
            </span>
          )}
          {action.due_date && (
            <span
              className={cn(
                'flex items-center gap-1',
                action.is_overdue && 'text-red-600',
              )}
            >
              {action.is_overdue && <AlertTriangle className="h-3.5 w-3.5" />}
              <Calendar className="h-3.5 w-3.5" />
              {new Date(action.due_date + 'T00:00:00').toLocaleDateString()}
              {action.is_overdue && (
                <Badge variant="outline" className="ml-1 px-1 py-0 text-xs text-red-600">
                  Overdue
                </Badge>
              )}
            </span>
          )}
          <span className="text-xs text-muted-foreground/60">
            Created {new Date(action.created_at).toLocaleDateString()}
          </span>
        </>
      }
    />
  )
}
